import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import DashboardLayout from 'layout/Dashboard';
import Loadable from 'components/Loadable';
import { FuzzScanTargetProvider } from 'context/FuzzScanTargetContext';
import ScanJobsPage from '../views/fuzzscan/ScanJobsPage';
import ScanSchedulesPage from '../views/fuzzscan/ScanSchedulesPage';
import ApiKeysPage from '../views/fuzzscan/ApiKeysPage';
import WebAuthProfilesPage from '../views/fuzzscan/WebAuthProfilesPage';
import CasesPage from '../views/fuzzscan/CasesPage';
import IntelFeedsPage from '../views/fuzzscan/IntelFeedsPage';
import RiskOverviewPage from '../views/fuzzscan/RiskOverviewPage';
import AnalyticsPage from '../views/fuzzscan/AnalyticsPage';
import ArtifactRegistryPage from '../views/fuzzscan/ArtifactRegistryPage';
import FindingFingerprintsPage from '../views/fuzzscan/FindingFingerprintsPage';
import OastMonitorPage from '../views/fuzzscan/OastMonitorPage';
import SupplyChainPage from '../views/fuzzscan/SupplyChainPage';
import FuzzingPage from '../views/fuzzscan/FuzzingPage';
import WebsiteChangesPage from '../views/fuzzscan/WebsiteChangesPage';
import AttackSurfacePage from '../views/fuzzscan/AttackSurfacePage';
import SbomPage from '../views/fuzzscan/SbomPage';
import SecretsDetectionPage from '../views/fuzzscan/SecretsDetectionPage';
import ApiSecurityPage from '../views/fuzzscan/ApiSecurityPage';
import EngagementsPage from '../views/fuzzscan/EngagementsPage';
import ExploitDbPage from '../views/fuzzscan/ExploitDbPage';
import ScanDiffPage from '../views/fuzzscan/ScanDiffPage';
import DataExportPage from '../views/fuzzscan/DataExportPage';
import MyWorkPage from '../views/fuzzscan/MyWorkPage';
import WebhooksPage from '../views/fuzzscan/WebhooksPage';
import ScanTemplatesPage from '../views/fuzzscan/ScanTemplatesPage';
import CloudPosturePage from '../views/fuzzscan/CloudPosturePage';
import DorkEnginePage from '../views/fuzzscan/DorkEnginePage';
import SurfaceSearchPage from '../views/fuzzscan/SurfaceSearchPage';
import AdAuditPage from '../views/fuzzscan/AdAuditPage';
import ExecutiveSummaryPage from '../views/fuzzscan/ExecutiveSummaryPage';
import Target360Page from '../views/fuzzscan/Target360Page';
import PassiveIntelPage from '../views/fuzzscan/PassiveIntelPage';
import BlacklistPage from '../views/fuzzscan/BlacklistPage';
import CvssCalculatorPage from '../views/fuzzscan/CvssCalculatorPage';
import SharedLinksPage from '../views/fuzzscan/SharedLinksPage';
import BillingQuotaPage from '../views/fuzzscan/BillingQuotaPage';
import ActiveSessionsPage from '../views/fuzzscan/ActiveSessionsPage';
import ConsoleKeysPage from '../views/fuzzscan/ConsoleKeysPage';

const AccountSettingsPage = Loadable(lazy(() => import('views/settings/AccountSettingsPage')));

const ScansPage = Loadable(lazy(() => import('views/fuzzscan/ScansPage')));
const ReconPage = Loadable(lazy(() => import('views/fuzzscan/ReconPage')));
const EnumPage = Loadable(lazy(() => import('views/fuzzscan/EnumPage')));
const VulnsPage = Loadable(lazy(() => import('views/fuzzscan/VulnsPage')));

const ReportingPage = Loadable(lazy(() => import('views/fuzzscan/ReportingPage')));
const UserDashboard = Loadable(lazy(() => import('views/dashboard/UserDashboard')));

const TargetsOverviewPage = Loadable(lazy(() => import('views/fuzzscan/TargetsOverviewPage')));
const MobileScansPage = Loadable(lazy(() => import('views/fuzzscan/MobileScansPage')));
const NetworkScansPage = Loadable(lazy(() => import('views/fuzzscan/NetworkScansPage')));
const CloudScansPage = Loadable(lazy(() => import('views/fuzzscan/CloudScansPage')));
const CveCatalogPage = Loadable(lazy(() => import('views/fuzzscan/CveCatalogPage')));
const ComplianceMatrixPage = Loadable(lazy(() => import('views/fuzzscan/ComplianceMatrixPage')));
const RemediationCenterPage = Loadable(lazy(() => import('views/fuzzscan/RemediationCenterPage')));
const VexAdvisoriesPage = Loadable(lazy(() => import('views/fuzzscan/VexAdvisoriesPage')));
const ThreatAlertsPage = Loadable(lazy(() => import('views/fuzzscan/ThreatAlertsPage')));
const NetworkMapPage = Loadable(lazy(() => import('views/fuzzscan/NetworkMapPage')));

const FuzzScanLayout = ({ children }) => <FuzzScanTargetProvider>{children}</FuzzScanTargetProvider>;

const FuzzScanRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: (
        <FuzzScanLayout>
          <DashboardLayout />
        </FuzzScanLayout>
      ),
      children: [
        { path: '/', element: <UserDashboard /> },
        { path: 'dashboard', element: <UserDashboard /> },

        { path: 'targets', element: <TargetsOverviewPage /> },
        { path: 'threat-alerts', element: <ThreatAlertsPage /> },
        { path: 'target-overview', element: <Target360Page /> },
        { path: 'targets/:targetId', element: <Navigate to="/targets" replace /> },
        {
          path: 'targets/:targetId/assets',
          element: <Navigate to="/targets" replace />
        },
        { path: 'web-scans', element: <ScansPage /> },
        { path: 'network-scans', element: <NetworkScansPage /> },
        { path: 'cloud-scans', element: <CloudScansPage /> },
        { path: 'cloud-service-scans', element: <CloudScansPage /> },
        { path: 'mobile-scans', element: <MobileScansPage /> },
        { path: 'oast-monitor', element: <OastMonitorPage /> },
        { path: 'recon', element: <ReconPage /> },
        { path: 'dorks', element: <DorkEnginePage /> },
        { path: 'surface-search', element: <SurfaceSearchPage /> },
        { path: 'passive-intel', element: <PassiveIntelPage /> },
        { path: 'blacklist', element: <BlacklistPage /> },
        { path: 'cvss-calculator', element: <CvssCalculatorPage /> },
        { path: 'enum', element: <EnumPage /> },
        { path: 'network-map', element: <NetworkMapPage /> },
        { path: 'vulns', element: <VulnsPage /> },
        { path: 'ad-audit', element: <AdAuditPage /> },
        { path: 'remediation', element: <RemediationCenterPage /> },
        { path: 'cves', element: <CveCatalogPage /> },
        { path: 'compliance', element: <ComplianceMatrixPage /> },
        { path: 'vex', element: <VexAdvisoriesPage /> },
        { path: 'risk', element: <RiskOverviewPage /> },
        { path: 'cases', element: <CasesPage /> },
        { path: 'intel/feeds', element: <IntelFeedsPage /> },
        { path: 'reporting', element: <ReportingPage /> },
        { path: 'executive', element: <ExecutiveSummaryPage /> },
        { path: 'analytics', element: <AnalyticsPage /> },
        { path: 'artifacts', element: <ArtifactRegistryPage /> },
        { path: 'supply-chain', element: <SupplyChainPage /> },
        { path: 'finding-fingerprints', element: <FindingFingerprintsPage /> },

        { path: 'fuzzing', element: <FuzzingPage /> },
        { path: 'attack-surface', element: <AttackSurfacePage /> },
        { path: 'website-changes', element: <WebsiteChangesPage /> },
        { path: 'sbom', element: <SbomPage /> },
        { path: 'secrets', element: <SecretsDetectionPage /> },
        { path: 'api-security', element: <ApiSecurityPage /> },
        { path: 'engagements', element: <EngagementsPage /> },
        { path: 'exploits', element: <ExploitDbPage /> },
        { path: 'knowledge-base', element: <Navigate to="/dashboard" replace /> },
        { path: 'notes', element: <Navigate to="/dashboard" replace /> },

        { path: 'scan-diff', element: <ScanDiffPage /> },
        { path: 'vuln-benchmark', element: <Navigate to="/scan-diff" replace /> },
        { path: 'exports', element: <DataExportPage /> },
        { path: 'my-work', element: <MyWorkPage /> },
        { path: 'webhooks', element: <WebhooksPage /> },
        { path: 'shared-links', element: <SharedLinksPage /> },
        { path: 'scan-templates', element: <ScanTemplatesPage /> },
        { path: 'cloud-posture', element: <CloudPosturePage /> },
        { path: 'team-activity', element: <Navigate to="/dashboard" replace /> },

        { path: 'assets', element: <Navigate to="/targets" replace /> },

        { path: 'schedules', element: <ScanSchedulesPage /> },
        { path: 'jobs', element: <ScanJobsPage /> },

        // Settings Page accounts

        { path: 'settings', element: <AccountSettingsPage /> },
        { path: 'settings/api-keys', element: <ApiKeysPage /> },
        { path: 'settings/third-party-keys', element: <ApiKeysPage /> },
        { path: 'settings/web-auth-profiles', element: <WebAuthProfilesPage /> },
        { path: 'settings/sessions', element: <ActiveSessionsPage /> },
        { path: 'settings/console-keys', element: <ConsoleKeysPage /> },
        { path: 'settings/billing', element: <BillingQuotaPage /> }
      ]
    }
  ]
};

export default FuzzScanRoutes;
